import {
  Component,
  computed,
  effect,
  Output,
  EventEmitter,
  Input,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  inject,
} from "@angular/core";
import { CoreService } from "src/app/services/core.service";
import { MatDialog } from "@angular/material/dialog";
import { navItems } from "../sidebar/sidebar-data";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { MaterialModule } from "src/app/material.module";
import { RouterModule } from "@angular/router";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgScrollbarModule } from "ngx-scrollbar";
import { AppSettings } from "src/app/config";
import { SessionService } from "src/app/services/session.service";
import { NotificationsComponent } from "src/app/shared/components/notifications/notifications.component";
import { WebSocketNotificationOrchestratorService } from "src/app/shared/services/websocket-notification-orchestrator.service";
import { ConfirmComponent } from "src/app/shared/components/confirm-generic/confirm.component";
import { NavService } from "src/app/services/nav.service";
import { HeaderSearchComponent } from "./header-search.component";
import { AppSearchDialogComponent } from "./search-dialog.component";
import { SearchService } from "src/app/services/search.service";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { BreadcrumbComponent } from "src/app/shared/components/breadcrumb/breadcrumb.component";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { isOpsAdminUser } from "src/app/pages/ops-ui/helpers/ops-ui-profile.helper";
import { ContactUsComponent } from "src/app/shared/components/contact-us/contact-us.component";
import { StoreService } from "src/app/services/apps/store/store.service";
import { LoaderService } from "src/app/shared/components/loader/loader.service";

@Component({
  selector: "app-header",
  imports: [
    RouterModule,
    CommonModule,
    NgScrollbarModule,
    TablerIconsModule,
    MaterialModule,
    TranslateModule,
    NotificationsComponent,
    BreadcrumbComponent,
    HeaderSearchComponent,
  ],
  templateUrl: "./header.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  protected sidenavCollapsedState = true;

  private readonly settings = inject(CoreService);
  private readonly cdr = inject(ChangeDetectorRef);
  private settingsSubscription = Subscription.EMPTY;
  private searchOpenSubscription = Subscription.EMPTY;
  private langSubscription: Subscription | null = null;
  private profileService = inject(ProfileService);
  private readonly searchService = inject(SearchService);

  readonly showOpsConsoleEntry = computed(() =>
    isOpsAdminUser(this.profileService.userProfile()),
  );

  public selectedLanguage: any = {
    language: "English",
    code: "en",
    type: "US",
    icon: "/assets/images/flag/icon-flag-en.svg",
  };

  public languages: any[] = [
    {
      language: "English",
      code: "en",
      type: "US",
      icon: "/assets/images/flag/icon-flag-en.svg",
    },
    {
      language: "Español",
      code: "es",
      icon: "/assets/images/flag/icon-flag-es.svg",
    },
    // {
    //   language: 'Français',
    //   code: 'fr',
    //   icon: '/assets/images/flag/icon-flag-fr.svg',
    // },
    // {
    //   language: 'German',
    //   code: 'de',
    //   icon: '/assets/images/flag/icon-flag-de.svg',
    // },
  ];

  @Output() optionsChange = new EventEmitter<AppSettings>();
  user = {
    initials: "",
    email: "",
    name: "",
  };
  constructor(
    public dialog: MatDialog,
    private translate: TranslateService,
    private sessionService: SessionService,
    private wsOrchestrator: WebSocketNotificationOrchestratorService,
    private storeService: StoreService,
    private loaderService: LoaderService,
  ) {
    effect(() => {
      const profile = this.profileService.userProfile();
      if (!profile) {
        this.user = {
          initials: "U",
          email: "",
          name: "User",
        };
        return;
      }

      const fullName = profile.name || profile.email || "";
      const names = fullName.trim().split(" ");
      const initials = names
        .map((n) => n[0]?.toUpperCase())
        .join("")
        .slice(0, 2);

      this.user = {
        initials: initials || profile.email?.charAt(0)?.toUpperCase() || "U",
        email: profile.email || "",
        name:
          fullName ||
          profile.email?.slice(0, profile.email.indexOf("@")) ||
          "User",
      };
    });
  }

  options = this.settings.getOptions();

  async ngOnInit() {
    this.refreshSidenavCollapsedState();
    this.settingsSubscription = this.settings.notify.subscribe(() => {
      this.refreshSidenavCollapsedState();
      this.cdr.markForCheck();
    });

    try {
      const defaultLang = this.settings.getLanguage();
      this.selectedLanguage =
        this.languages.find((lang) => lang.code === defaultLang) ||
        this.languages[0];
      this.translate.setDefaultLang(defaultLang);
      this.translate.use(defaultLang);

      if (!this.profileService.userProfile()) {
        this.profileService.getUserProfile().subscribe();
      }
    } catch (error) {
      console.error("Error initializing header component:", error);
      // Fallback en caso de error
      this.user = {
        initials: "U",
        email: "",
        name: "User",
      };
    }

    this.langSubscription = this.translate.onLangChange.subscribe((event) => {
      const selectedLang = this.languages.find(
        (lang) => lang.code === event.lang,
      );
      if (selectedLang) {
        this.selectedLanguage = selectedLang;
      }
    });

    this.searchOpenSubscription = this.searchService.openSearch$.subscribe(
      () => {
        if (!this.showToggle) {
          this.openMobileSearch();
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.settingsSubscription.unsubscribe();
    this.searchOpenSubscription.unsubscribe();

    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  openMobileSearch(): void {
    this.dialog.open(AppSearchDialogComponent, {
      panelClass: "search-dialog-panel",
      autoFocus: true,
      width: "min(600px, calc(100vw - 1rem))",
      maxWidth: "calc(100vw - 1rem)",
    });
  }

  private refreshSidenavCollapsedState(): void {
    this.sidenavCollapsedState = this.settings.getOptions().sidenavCollapsed;
  }

  async logOut() {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      data: {
        title: this.translate.instant("header.logout_confirm_title"),
        message: this.translate.instant("header.logout_confirm_message"),
        okAction: this.translate.instant("header.logout_ok"),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.sessionService.signOutUser().then(() => {
          this.wsOrchestrator.shutdown();
        });
      }
    });
  }

  onSupport(): void {
    this.loaderService.show();
    this.storeService
      .get()
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: (stores: unknown) => {
          this.dialog.open(ContactUsComponent, {
            autoFocus: false,
            disableClose: true,
            data: { stores },
          });
        },
      });
  }

  changeLanguage(lang: any): void {
    this.translate.use(lang.code);
    this.selectedLanguage = lang;
    this.settings.setLanguage(lang.code);
  }

  setlightDark(theme: string) {
    this.options.theme = theme;
    this.settings.setOptions(this.options);
    this.emitOptions();
  }

  private emitOptions() {
    this.optionsChange.emit(this.options);
  }
}
